import {ISODate} from "./date"

type TuplableType = string | number | boolean | null | undefined | ISODate | ReadonlyArray<TuplableType>

type AllTuplesMap = [
  Map<TuplableType, AllTuplesMap>,
  Tuple<ReadonlyArray<TuplableType>> | undefined
]

const allTuples: AllTuplesMap = [new Map(), undefined]

export type Tuple<T extends ReadonlyArray<TuplableType>> = T & {isTuple: true}

export function tuple<T extends ReadonlyArray<TuplableType>>(args: T): Tuple<T> {
  /* This tuple will make sure that two tuples with the same content will be the
   * same tuple.
   * In order to do so, it WILL have to store anything that was ever made into
   * a tuple, so it is a memory leak
   */
  let root = allTuples
  args.forEach(arg => {
    if (!root[0].has(arg)) {
      root[0].set(arg, [new Map(), undefined])
    }
    root = root[0].get(arg)!
  })
  if (root[1] === undefined) {
    // @ts-ignore
    const myTuple: Tuple<T> =
      Object.defineProperty([...args], "isTuple", {value: true})
    Object.freeze(myTuple)
    root[1] = myTuple
  }
  return root[1] as Tuple<T>
}

export function compare<T extends ReadonlyArray<TuplableType>>(
    a: Tuple<T>, b: Tuple<T>): number {
      return compareWithDirection([])(a, b)
    }
export function compareWithDirection<T extends ReadonlyArray<TuplableType>>(
  reverse: ReadonlyArray<boolean>
): (a: Tuple<T>, b: Tuple<T>) => number {
  /*
   * Defines a concrete order
   * if fields have different type, then in order in TuplableType
   * if one of tuples runs out of items, that one sorts earlier
   * string using localeCompare
   * number -Inf < 0 < Inf < Nan ( https://stackoverflow.com/a/17557983/1207489 )
   * false < true
   * tuples recursively compare
   */

  const typecheckers: ((el: TuplableType) => boolean)[] = [
    el => typeof el === "string",
    el => typeof el === "number",
    el => el === true || el === false,
    el => el === null,
    el => el === undefined,
    el => el instanceof ISODate,
    el => el instanceof Array
  ]

  const comparers: ((a: any, b: any) => number)[] = [
    (a, b) => (a as string).localeCompare(b),
    (a, b) => isNaN(a) ? (isNaN(b) ? 0 : 1) : a - b,
    (a, b) => a - b,
    () => 0,  // null is always equal
    () => 0,  // undefined is always equal
    ISODate.compare,
    compare,

  ]

  return (a, b) => {
    for (let i = 0; i < Math.max(a.length, b.length); i++) {
      if (i >= a.length) {
        return -1;
      }
      if (i >= b.length) {
        return 1
      }
      const result_a = typecheckers.findIndex(checker => checker(a[i]))
      const result_b = typecheckers.findIndex(checker => checker(b[i]))
      if (result_a === -1 || result_b === -1) {
        console.log(a, b, i)
        throw new Error(`Error in compare: ${a}, ${b}, ${i}`)
      }
      let result = result_a - result_b
      if (result) {
        return result
      }
      result = comparers[result_a](a[i], b[i])
      if (reverse[i] ?? false) {
        result = -result
      }
      if (result) {
        return result
      }
    }
    return 0
  }
}
